/**
 * <cd-bundle-builder>
 *
 * Powers the build-your-bundle page (sections/cd-bundle-builder.liquid).
 *
 * Selection model:
 *   - this.selected: Array<{ productId, title, juniorVariantId, refillVariantId, image, url }>
 *   - First selected painting → Junior Artist (full supplies)
 *   - Remaining N-1 paintings → Refill (paint + canvas only)
 *   - Order in array = order in cart (so first card the customer
 *     picked will be the JA kit, which is the natural mental model:
 *     "this is the painting I'm starting with").
 *
 * Add-to-cart payload (POST /cart/add.js):
 *   {
 *     items: [
 *       { id: <JA variant>, quantity: 1, properties: {
 *           _bundle_id: 'bundle_xyz123',
 *           _bundle_name: '<optional nickname>',
 *           _bundle_role: 'main'
 *       }},
 *       { id: <Refill variant>, quantity: 1, properties: {
 *           _bundle_id: 'bundle_xyz123',
 *           _bundle_name: '<optional nickname>',
 *           _bundle_role: 'refill'
 *       }},
 *       ...
 *     ]
 *   }
 *
 * Properties prefixed with `_` are hidden from the customer (and the
 * order-confirmation email) but visible to the merchant on the order.
 * The `_bundle_id` is what the cart drawer + packing slip use to group
 * items together.
 */
(function () {
  if (customElements.get('cd-bundle-builder')) return;

  function generateBundleId() {
    return 'bundle_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  class CDBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.targetCount = parseInt(this.getAttribute('data-target-count'), 10) || 4;
      this.bundlePrice = this.getAttribute('data-bundle-price') || '';

      this.cards = Array.from(this.querySelectorAll('[data-cd-bb-card]'));
      this.countEl = this.querySelector('[data-cd-bb-count]');
      this.count2El = this.querySelector('[data-cd-bb-count2]');
      this.slotsWrap = this.querySelector('[data-cd-bb-slots]');
      this.slots = Array.from(this.querySelectorAll('[data-cd-bb-slot]'));
      this.summaryEl = this.querySelector('[data-cd-bb-summary]');
      this.gridEl = this.querySelector('.cd-bundle-builder__grid');
      this.addBtn = this.querySelector('[data-cd-bb-add]');
      this.clearBtn = this.querySelector('[data-cd-bb-clear]');

      if (!this.cards.length || !this.addBtn) return;

      this.selected = [];
      this.busy = false;
      this.gridInView = false;

      this.cards.forEach(this.bindCard.bind(this));
      this.addBtn.addEventListener('click', this.onAdd.bind(this));
      if (this.clearBtn) this.clearBtn.addEventListener('click', this.clearSelections.bind(this));

      this.setupTracking();
      this.setupVisibility();
      this.refreshUI();
    }

    // "Started Bundle" tracking (assets/cd-bundle-tracking.js). Creates a tracker
    // bound to this builder, and — if the page was opened from a restore link
    // (?bundle=id,id,...) — pre-selects those paintings without firing an event.
    setupTracking() {
      this.tracker = (window.CDBundleTracking && window.CDBundleTracking.create)
        ? window.CDBundleTracking.create({ builderType: 'build-your-own' })
        : null;
      if (!this.tracker) return;
      var restore = this.tracker.getRestoreItems();
      if (!restore || !restore.length) return;
      var self = this;
      this.tracker.beginRestore();
      restore.forEach(function (it) {
        var card = self.cards.find(function (c) { return c.getAttribute('data-product-id') === String(it.id); });
        if (!card) return;
        var btn = card.querySelector('button.cd-bundle-builder__card');
        if (btn && btn.getAttribute('aria-pressed') !== 'true') btn.click();
      });
      this.tracker.endRestore();
    }

    // Report the current selection. The builder owns "complete" (count === target
    // today; a richer rule later needs no change in the tracker). Quantity is 1
    // per painting until flexible quantities ship.
    trackSelection() {
      if (!this.tracker) return;
      var items = this.selected.map(function (item) {
        return {
          id: item.productId,
          title: item.title,
          url: item.url,
          image: item.image,
          variantId: item.juniorVariantId,
          quantity: 1
        };
      });
      this.tracker.report(items, {
        isComplete: this.selected.length === this.targetCount,
        target: this.targetCount,
        bundlePrice: this.bundlePrice   // fixed advertised label, e.g. "$99" (data-bundle-price)
      });
    }

    // Contextual sticky visibility: the tray is only relevant once the
    // picker grid is on screen — before that the customer is still on the
    // hero + upper sections, and a floating "Pick 4" bar is just noise.
    // Once anything is selected we keep it up regardless (so they can
    // finish after scrolling down to reviews / FAQ).
    setupVisibility() {
      if (!this.summaryEl) return;
      if (!this.gridEl || typeof IntersectionObserver === 'undefined') {
        // No grid or no IO support — fall back to always visible.
        this.gridInView = true;
        this.updateVisibility();
        return;
      }
      var self = this;
      this.io = new IntersectionObserver(
        function (entries) {
          self.gridInView = entries[0].isIntersecting;
          self.updateVisibility();
        },
        // Start a little before the grid's top edge reaches the viewport
        // so the tray is ready as the first row appears.
        { rootMargin: '0px 0px -8% 0px', threshold: 0 }
      );
      this.io.observe(this.gridEl);
    }

    updateVisibility() {
      if (!this.summaryEl) return;
      var show = this.gridInView || this.selected.length > 0;
      this.summaryEl.classList.toggle('is-visible', show);
    }

    bindCard(cardWrap) {
      var btn = cardWrap.querySelector('button.cd-bundle-builder__card');
      if (!btn) return;
      var self = this;
      btn.addEventListener('click', function () {
        var pid = cardWrap.getAttribute('data-product-id');
        var idx = self.selected.findIndex(function (item) { return item.productId === pid; });
        if (idx >= 0) {
          // Already selected → toggle off.
          self.selected.splice(idx, 1);
          btn.setAttribute('aria-pressed', 'false');
        } else {
          if (self.selected.length >= self.targetCount) {
            // At cap — gentle bounce on the summary so user notices.
            self.bounceSummary();
            return;
          }
          self.selected.push({
            productId: pid,
            title: cardWrap.getAttribute('data-product-title'),
            juniorVariantId: cardWrap.getAttribute('data-junior-variant-id'),
            refillVariantId: cardWrap.getAttribute('data-refill-variant-id'),
            url: cardWrap.getAttribute('data-product-url'),
            image: cardWrap.getAttribute('data-image')
          });
          btn.setAttribute('aria-pressed', 'true');
        }
        self.refreshUI();
      });
    }

    bounceSummary() {
      var el = this.querySelector('[data-cd-bb-summary]');
      if (!el) return;
      el.classList.remove('is-bouncing');
      // Force reflow so the animation re-fires even on rapid clicks.
      void el.offsetWidth;
      el.classList.add('is-bouncing');
    }

    refreshUI() {
      var n = this.selected.length;
      var target = this.targetCount;
      if (this.countEl) this.countEl.textContent = String(n);
      if (this.count2El) this.count2El.textContent = String(n);
      if (this.clearBtn) this.clearBtn.hidden = n === 0;

      // The slots themselves are the progress indicator (filled vs empty),
      // so the container carries the progressbar semantics.
      if (this.slotsWrap) this.slotsWrap.setAttribute('aria-valuenow', String(n));

      this.renderSlots();

      // Dynamic CTA: guide the customer to completion, then let them add.
      var complete = n === target;
      if (!this.busy) {
        if (complete) {
          // Price is always shown in the bar meta, so the button is pure action.
          this.addBtn.textContent = 'Add to cart';
        } else {
          var remaining = target - n;
          this.addBtn.textContent = 'Pick ' + remaining + (remaining === 1 ? ' more painting' : ' more paintings');
        }
      }
      this.addBtn.disabled = !complete || this.busy;

      this.updateVisibility();
      this.trackSelection();
    }

    // Fill each slot with the matching selected painting's thumbnail (plus
    // a remove control), or reset it to a numbered empty placeholder.
    renderSlots() {
      var self = this;
      for (var i = 0; i < this.slots.length; i++) {
        var slot = this.slots[i];
        var item = this.selected[i];
        // Clear existing contents.
        while (slot.firstChild) slot.removeChild(slot.firstChild);

        if (!item) {
          slot.classList.remove('is-filled');
          var num = document.createElement('span');
          num.className = 'cd-bundle-builder__slot-num';
          num.setAttribute('aria-hidden', 'true');
          num.textContent = String(i + 1);
          slot.appendChild(num);
          continue;
        }

        slot.classList.add('is-filled');
        if (item.image) {
          var img = document.createElement('img');
          img.className = 'cd-bundle-builder__slot-img';
          img.src = item.image;
          img.alt = item.title || '';
          img.loading = 'lazy';
          img.decoding = 'async';
          slot.appendChild(img);
        } else {
          // No image on the product — show its initial rather than a blank.
          var initial = document.createElement('span');
          initial.className = 'cd-bundle-builder__slot-num';
          initial.setAttribute('aria-hidden', 'true');
          initial.textContent = (item.title || '?').charAt(0).toUpperCase();
          slot.appendChild(initial);
        }

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cd-bundle-builder__slot-remove';
        remove.setAttribute('aria-label', 'Remove ' + (item.title || 'painting'));
        remove.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>';
        (function (pid) {
          remove.addEventListener('click', function (e) {
            e.stopPropagation();
            self.removeSelection(pid);
          });
        })(item.productId);
        slot.appendChild(remove);
      }
    }

    // Remove one painting by product id (from a slot's × control) and
    // un-press its card so the grid and tray stay in sync.
    removeSelection(pid) {
      var idx = this.selected.findIndex(function (item) { return item.productId === pid; });
      if (idx < 0) return;
      this.selected.splice(idx, 1);
      var cardWrap = this.cards.find(function (c) { return c.getAttribute('data-product-id') === pid; });
      if (cardWrap) {
        var btn = cardWrap.querySelector('button.cd-bundle-builder__card');
        if (btn) btn.setAttribute('aria-pressed', 'false');
      }
      this.refreshUI();
    }

    clearSelections() {
      this.selected = [];
      this.cards.forEach(function (cardWrap) {
        var btn = cardWrap.querySelector('button.cd-bundle-builder__card');
        if (btn) btn.setAttribute('aria-pressed', 'false');
      });
      this.refreshUI();
    }

    async onAdd() {
      if (this.busy || this.selected.length !== this.targetCount) return;
      this.busy = true;
      var origLabel = this.addBtn.textContent;
      this.addBtn.textContent = 'Adding…';
      this.addBtn.disabled = true;

      var bundleId = generateBundleId();

      var items = this.selected.map(function (item, i) {
        return {
          id: parseInt(i === 0 ? item.juniorVariantId : item.refillVariantId, 10),
          quantity: 1,
          properties: { _bundle_id: bundleId, _bundle_role: i === 0 ? 'main' : 'refill' }
        };
      });

      // Collect section IDs that need to refresh on cart update — same
      // pattern Horizon's product-form.js uses. We pass these to
      // /cart/add.js so the response includes pre-rendered HTML for
      // the cart drawer + cart bubble + free-shipping bar, then we
      // dispatch the section payload on `cart:update` so each component
      // can morph itself without a separate fetch.
      //
      // Walk UP from each cart-related element to its enclosing
      // shopify-section-* wrapper. The custom-element nodes themselves
      // (cart-drawer-component, cart-icon-component) don't carry
      // dataset.sectionId, so the previous querySelectorAll-then-read-
      // sectionId approach silently dropped them.
      var sectionIdSet = new Set();
      document.querySelectorAll(
        'cart-drawer-component, cart-drawer, component-cart-items, cart-icon-component, cart-icon, cart-bubble, cd-shipping-progress, [data-section-rendering-key]'
      ).forEach(function (el) {
        var dsId = el.dataset && (el.dataset.sectionId || el.dataset.sectionRenderingKey);
        if (dsId) {
          sectionIdSet.add(dsId);
          return;
        }
        var wrap = el.closest('[id^="shopify-section-"]');
        if (wrap) sectionIdSet.add(wrap.id.replace(/^shopify-section-/, ''));
      });
      var sectionIds = Array.from(sectionIdSet);

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: items,
            sections: sectionIds.length ? sectionIds.join(',') : undefined
          })
        });
        if (!res.ok) {
          var errText = await res.text().catch(function () { return ''; });
          throw new Error('Cart add failed: HTTP ' + res.status + ' — ' + errText.slice(0, 200));
        }
        var json = await res.json();

        // Fetch the latest cart resource so the event payload includes
        // the full cart object (some components rely on detail.resource
        // rather than re-fetching).
        var cart = null;
        try {
          var cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (cartRes.ok) cart = await cartRes.json();
        } catch (_) {}

        // Publish `product_added_to_cart` to Shopify's web-pixels-manager
        // for each added line so Meta Pixel / CAPI and Google Ads /
        // GA4 see the AddToCart. Standard product forms auto-emit this;
        // our raw fetch('/cart/add.js') does not, so we do it manually
        // per Shopify's emitting-data docs.
        try {
          var currencyCode = (cart && cart.currency) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
          var addedItems = (json && json.items) || (Array.isArray(json) ? json : [json]);
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function' && Array.isArray(addedItems)) {
            addedItems.forEach(function (line) {
              if (!line) return;
              var unitAmount = (line.final_price != null ? line.final_price : line.price) / 100;
              var lineAmount = (line.final_line_price != null ? line.final_line_price : (unitAmount * (line.quantity || 1) * 100)) / 100;
              window.Shopify.analytics.publish('product_added_to_cart', {
                cartLine: {
                  cost: { totalAmount: { amount: lineAmount, currencyCode: currencyCode } },
                  merchandise: {
                    id: 'gid://shopify/ProductVariant/' + (line.variant_id || line.id),
                    productId: 'gid://shopify/Product/' + line.product_id,
                    title: line.variant_title || '',
                    vendor: line.vendor || '',
                    image: line.featured_image && line.featured_image.url ? { src: line.featured_image.url } : undefined,
                    price: { amount: unitAmount, currencyCode: currencyCode },
                    product: {
                      id: 'gid://shopify/Product/' + line.product_id,
                      title: line.product_title || line.title || '',
                      vendor: line.vendor || '',
                      type: line.product_type || '',
                      url: line.url || '',
                      untranslatedTitle: line.product_title || line.title || ''
                    }
                  },
                  quantity: line.quantity || 1
                }
              });
            });
          }
        } catch (analyticsErr) {
          console.warn('cd-bundle-builder: analytics publish failed', analyticsErr);
        }

        // Dispatch the canonical cart-update event with the section
        // HTML so component-cart-items.js morphs the drawer in place
        // (no second network round-trip).
        var updateEvent = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'cd-bundle-builder',
            data: {
              source: 'cd-bundle-builder',
              sections: json.sections || {},
              itemCount: items.length
            }
          }
        });
        document.dispatchEvent(updateEvent);

        // Open the cart drawer. Horizon's cart-drawer-component opens
        // automatically on cart:update only when its `auto-open` attr
        // is set; trigger explicitly to be sure.
        var drawer = document.querySelector('cart-drawer-component, cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else {
          var trigger = document.querySelector('[aria-controls="cart-drawer"], .header-actions__cart');
          if (trigger) trigger.click();
        }

        // Reset the picker so it's ready for another use, even though
        // we're not surfacing a multi-bundle prompt right now.
        this.clearSelections();
      } catch (err) {
        if (window.console && console.warn) console.warn('[cd-bundle-builder] add failed:', err);
        alert("We couldn't add the bundle to your cart. Please try again.");
      } finally {
        this.busy = false;
        this.addBtn.textContent = origLabel;
        this.refreshUI();
      }
    }
  }

  customElements.define('cd-bundle-builder', CDBundleBuilder);
})();
